import { useCallback, useEffect, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, AccountInfo } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from '@/config/msalConfig';
import { acquireToken } from '@/auth/acquireToken';
import { useAuthStore, UserClaims } from '@/stores/authStore';

/**
 * Extract user claims from MSAL account info.
 * Captures tenantId for Discovery analysis scoping.
 */
function extractClaims(account: AccountInfo): UserClaims {
  const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
  const emails = idTokenClaims?.emails as string[] | undefined;

  return {
    userId: (idTokenClaims?.oid || idTokenClaims?.sub || account.localAccountId) as string,
    email: (emails?.[0] || account.username) as string,
    displayName: (idTokenClaims?.name || account.name || '') as string,
    tenantId: (idTokenClaims?.tid || account.tenantId || '') as string,
    roles: (idTokenClaims?.roles || []) as string[],
  };
}

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const { user, isAuthenticated, isLoading, setUser, clearUser, setLoading } = useAuthStore();
  const initAttempted = useRef(false);

  // Sync MSAL state with Zustand store
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (initAttempted.current) return;
    initAttempted.current = true;

    if (isMsalAuthenticated && accounts.length > 0) {
      const claims = extractClaims(accounts[0]);
      setUser(claims);
      setLoading(false);
    } else if (!isMsalAuthenticated) {
      clearUser();
      setLoading(false);
    }
  }, [isMsalAuthenticated, accounts, inProgress, setUser, clearUser, setLoading]);

  // Reset init flag when accounts change
  useEffect(() => {
    initAttempted.current = false;
  }, [accounts.length]);

  const login = useCallback(async () => {
    try {
      setLoading(true);
      const response = await instance.loginPopup(loginRequest);
      if (response.account) {
        instance.setActiveAccount(response.account);
        const claims = extractClaims(response.account);
        setUser(claims);
      }
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      throw error;
    }
  }, [instance, setUser, setLoading]);

  const loginRedirect = useCallback(async () => {
    try {
      setLoading(true);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect failed:', error);
      setLoading(false);
      throw error;
    }
  }, [instance, setLoading]);

  const logout = useCallback(async () => {
    try {
      clearUser();
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance, clearUser]);

  const logoutRedirect = useCallback(async () => {
    try {
      clearUser();
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout redirect failed:', error);
      throw error;
    }
  }, [instance, clearUser]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) return null;

    try {
      // Robust acquisition: silent refresh, then a GUARDED interactive redirect
      // on interaction-required (SPA-Auth Standard rules 2 & 4). Never dead-ends
      // and never signs the user out — that was the token-refresh bug.
      return await acquireToken(instance, { ...tokenRequest, account: activeAccount });
    } catch (error) {
      // Genuine (non-interaction) failure — surface as null without clearing the
      // session. Interaction-required is recovered via redirect inside acquireToken.
      console.error('[useAuth] token acquisition failed:', error);
      return null;
    }
  }, [instance]);

  const forceLogout = useCallback(() => {
    clearUser();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    window.location.href = '/login';
  }, [clearUser]);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || inProgress !== InteractionStatus.None,
    login,
    loginRedirect,
    logout,
    logoutRedirect,
    getAccessToken,
    forceLogout,
  };
}
